import {
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import {
  CELL_WIDTH,
  BLOCK_MARGIN,
} from '../utils/gridConstants';

/**
 * Custom hook for unified resize functionality (mouse + touch).
 * Uses PointerEvents API for cross-platform compatibility.
 *
 * @param {Object} options - Configuration options
 * @param {string|number} options.blockId - ID of the block being resized
 * @param {number} options.startCol - Starting column index
 * @param {number} options.endCol - Ending column index
 * @param {number} options.x - Current x position in pixels
 * @param {number} options.width - Current width in pixels
 * @param {number} options.cellWidth - Width of each grid cell (default: CELL_WIDTH)
 * @param {number} options.weeksLength - Total number of weeks in the grid
 * @param {Function} options.onResizeStart - Called when resize starts
 * @param {Function} options.onResize - Called during resize with new column range
 * @param {Function} options.onResizeEnd - Called when resize ends
 * @param {boolean} options.disabled - Whether resize is disabled
 * @returns {Object} Resize state and handle props
 */
const usePlannerResize = ({
  blockId,
  startCol,
  endCol,
  x,
  width,
  cellWidth = CELL_WIDTH,
  weeksLength,
  onResizeStart,
  onResize,
  onResizeEnd,
  disabled = false,
}) => {
  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null); // 'left' | 'right'

  // Temporary visual state during resize
  const [tempX, setTempX] = useState(null);
  const [tempWidth, setTempWidth] = useState(null);

  // Refs for tracking resize
  const resizeStartRef = useRef(null);

  // Track when resize ended to prevent click-through
  const resizeEndTimeRef = useRef(0);

  /**
   * Handle pointer down on left resize handle
   */
  const handleLeftPointerDown = useCallback((e) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();

    // Capture pointer for tracking outside element
    e.target.setPointerCapture(e.pointerId);

    const { clientX } = e;

    resizeStartRef.current = {
      direction: 'left',
      startClientX: clientX,
      startCol,
      endCol,
      startX: x,
      startWidth: width,
      pointerId: e.pointerId,
    };

    setIsResizing(true);
    setResizeDirection('left');
    setTempX(x);
    setTempWidth(width);

    if (onResizeStart) {
      onResizeStart(blockId, 'left');
    }
  }, [disabled, startCol, endCol, x, width, blockId, onResizeStart]);

  /**
   * Handle pointer down on right resize handle
   */
  const handleRightPointerDown = useCallback((e) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();

    // Capture pointer for tracking outside element
    e.target.setPointerCapture(e.pointerId);

    const { clientX } = e;

    resizeStartRef.current = {
      direction: 'right',
      startClientX: clientX,
      startCol,
      endCol,
      startX: x,
      startWidth: width,
      pointerId: e.pointerId,
    };

    setIsResizing(true);
    setResizeDirection('right');
    setTempX(x);
    setTempWidth(width);

    if (onResizeStart) {
      onResizeStart(blockId, 'right');
    }
  }, [disabled, startCol, endCol, x, width, blockId, onResizeStart]);

  /**
   * Handle pointer move during resize
   */
  const handlePointerMove = useCallback((e) => {
    if (!isResizing || !resizeStartRef.current) return;

    const {
      direction,
      startClientX,
      startX: origX,
      startWidth: origWidth,
      startCol: origStartCol,
    } = resizeStartRef.current;
    const deltaX = e.clientX - startClientX;

    if (direction === 'left') {
      // Smooth visual feedback - follow the cursor exactly
      // Clamp to not go past the right edge or before column 0
      const minX = BLOCK_MARGIN; // Can't go before first column
      const maxX = origX + origWidth - cellWidth; // Must keep at least 1 week

      const newX = Math.max(minX, Math.min(maxX, origX + deltaX));
      const newWidth = origWidth - (newX - origX);

      setTempX(newX);
      setTempWidth(newWidth);
    } else if (direction === 'right') {
      // Smooth visual feedback - follow the cursor exactly
      // Clamp to keep at least 1 week and not exceed grid
      const minWidth = cellWidth - BLOCK_MARGIN * 2; // At least 1 week
      const maxWidth = (weeksLength - origStartCol) * cellWidth - BLOCK_MARGIN * 2;

      const newWidth = Math.max(minWidth, Math.min(maxWidth, origWidth + deltaX));

      setTempWidth(newWidth);
    }
  }, [isResizing, cellWidth, weeksLength]);

  /**
   * Handle pointer up to end resize
   */
  const handlePointerUp = useCallback((e) => {
    if (!isResizing || !resizeStartRef.current) return;

    // Release pointer capture
    try {
      e.target.releasePointerCapture(resizeStartRef.current.pointerId);
    } catch (err) {
      // Ignore if already released
    }

    const {
      direction,
      startClientX,
      startCol: origStartCol,
      endCol: origEndCol,
    } = resizeStartRef.current;
    const deltaX = e.clientX - startClientX;
    const deltaCol = Math.round(deltaX / cellWidth);

    let newStartCol = origStartCol;
    let newEndCol = origEndCol;

    if (direction === 'left') {
      newStartCol = Math.max(0, Math.min(origEndCol, origStartCol + deltaCol));
    } else if (direction === 'right') {
      newEndCol = Math.max(origStartCol, Math.min(weeksLength - 1, origEndCol + deltaCol));
    }

    // Only call onResize if the columns actually changed
    if (newStartCol !== origStartCol || newEndCol !== origEndCol) {
      if (onResize) {
        onResize(blockId, newStartCol, newEndCol);
      }
    }

    // Record when resize ended (used to prevent click-through)
    resizeEndTimeRef.current = Date.now();

    // Reset state
    setIsResizing(false);
    setResizeDirection(null);
    setTempX(null);
    setTempWidth(null);
    resizeStartRef.current = null;

    if (onResizeEnd) {
      onResizeEnd(blockId);
    }
  }, [isResizing, cellWidth, weeksLength, blockId, onResize, onResizeEnd]);

  /**
   * Handle pointer cancel (e.g., touch cancel)
   * Notifies parent that resize ended (was cancelled) and resets state.
   */
  const handlePointerCancel = useCallback(() => {
    // Record when resize ended (used to prevent click-through)
    resizeEndTimeRef.current = Date.now();

    // Reset state
    setIsResizing(false);
    setResizeDirection(null);
    setTempX(null);
    setTempWidth(null);
    resizeStartRef.current = null;

    // Notify parent that resize ended (no changes were committed)
    if (onResizeEnd) {
      onResizeEnd(blockId);
    }
  }, [blockId, onResizeEnd]);

  // Add document-level listeners when resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerCancel);

      return () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
      };
    }
    return undefined;
  }, [isResizing, handlePointerMove, handlePointerUp, handlePointerCancel]);

  /**
   * Check if a resize just ended recently (within threshold ms).
   * Used to prevent click events that fire immediately after resize end.
   */
  const justFinishedResizing = useCallback((thresholdMs = 100) => (
    Date.now() - resizeEndTimeRef.current < thresholdMs
  ), []);

  return {
    // State
    isResizing,
    resizeDirection,

    // Computed values (use these for display during resize)
    displayX: isResizing && tempX !== null ? tempX : x,
    displayWidth: isResizing && tempWidth !== null ? tempWidth : width,

    // Props to spread on left resize handle
    leftHandleProps: {
      onPointerDown: handleLeftPointerDown,
      style: { touchAction: 'none' },
    },

    // Props to spread on right resize handle
    rightHandleProps: {
      onPointerDown: handleRightPointerDown,
      style: { touchAction: 'none' },
    },

    // Check if resize just finished (to prevent click-through)
    justFinishedResizing,
  };
};

export default usePlannerResize;
